import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { io } from 'socket.io-client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatRoom() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token } = useAuth();

  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [job, setJob] = useState(null);

  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch chat and messages
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch chat
        const chatResponse = await fetch(`${API_URL}/api/chats/${chatId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });

        if (!chatResponse.ok) throw new Error('Chat not found');
        const chatData = await chatResponse.json();
        setChat(chatData);

        // Fetch messages
        const messagesResponse = await fetch(`${API_URL}/api/chats/${chatId}/messages`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });

        if (messagesResponse.ok) {
          const messagesData = await messagesResponse.json();
          setMessages(messagesData);
        }

        // Fetch other user
        const otherId = chatData.participants.find(p => p !== user?.user_id);
        if (otherId) {
          const userResponse = await fetch(`${API_URL}/api/users/${otherId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          });
          if (userResponse.ok) {
            setOtherUser(await userResponse.json());
          }
        }

        // Fetch job
        if (chatData.job_id) {
          const jobResponse = await fetch(`${API_URL}/api/jobs/${chatData.job_id}`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          });
          if (jobResponse.ok) {
            setJob(await jobResponse.json());
          }
        }
      } catch (error) {
        console.error('Error fetching chat:', error);
        toast.error(t('common.error'));
        navigate('/messages');
      } finally {
        setLoading(false);
      }
    };

    if (token && chatId) {
      fetchData();
    }
  }, [chatId, token, user?.user_id, navigate, t]);

  // Socket connection
  useEffect(() => {
    if (!chatId || !token) return;

    // Connect to Socket.IO
    socketRef.current = io(API_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      console.log('Socket connected');
      socketRef.current.emit('join_chat', { chat_id: chatId });
    });

    socketRef.current.on('new_message', (message) => {
      setMessages(prev => {
        // Prevent duplicates
        if (prev.some(m => m.message_id === message.message_id)) {
          return prev;
        }
        return [...prev, message];
      });
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_chat', { chat_id: chatId });
        socketRef.current.disconnect();
      }
    };
  }, [chatId, token]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/chats/${chatId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: newMessage.trim() }),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to send message');

      const message = await response.json();
      
      // Add message locally (socket will also receive it)
      setMessages(prev => {
        if (prev.some(m => m.message_id === message.message_id)) {
          return prev;
        }
        return [...prev, message];
      });

      setNewMessage('');
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString();
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.created_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-4 shrink-0">
        <button
          onClick={() => navigate('/messages')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>

        <Avatar className="h-10 w-10">
          <AvatarImage src={otherUser?.photo_url} />
          <AvatarFallback className="bg-slate-200">
            {otherUser?.name?.charAt(0)?.toUpperCase() || '?'}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 truncate">{otherUser?.name || 'Unknown'}</p>
          {job && (
            <Badge 
              variant="secondary" 
              className="text-xs cursor-pointer"
              onClick={() => navigate(`/jobs/${job.job_id}`)}
            >
              {job.title}
            </Badge>
          )}
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {Object.entries(groupedMessages).map(([date, msgs]) => (
          <div key={date}>
            <div className="flex justify-center mb-4">
              <span className="bg-slate-200 text-slate-600 text-xs px-3 py-1 rounded-full">
                {date}
              </span>
            </div>
            <div className="space-y-3">
              {msgs.map((message) => {
                const isOwn = message.sender_id === user?.user_id;
                return (
                  <div
                    key={message.message_id}
                    data-testid={`message-${message.message_id}`}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] px-4 py-3 rounded-2xl ${
                        isOwn
                          ? 'bg-slate-900 text-white rounded-br-sm'
                          : 'bg-white text-slate-900 border border-slate-200 rounded-bl-sm'
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words">{message.content}</p>
                      <p className={`text-xs mt-1 ${isOwn ? 'text-slate-400' : 'text-slate-500'}`}>
                        {formatTime(message.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form
        onSubmit={handleSendMessage}
        className="bg-white border-t border-slate-200 p-4 flex gap-3 shrink-0"
      >
        <Input
          data-testid="chat-message-input"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={t('chat.type_message')}
          className="flex-1 py-5"
          disabled={sending}
        />
        <Button
          type="submit"
          data-testid="send-message-btn"
          disabled={!newMessage.trim() || sending}
          className="bg-slate-900 hover:bg-slate-800 px-6"
        >
          {sending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </Button>
      </form>
    </div>
  );
}
